import { useEffect, useState, type FormEvent } from 'react';
import { ErrorState, Loading } from '../components/States';
import { HomepageHeroMedia } from '../components/HomepageHeroMedia';
import {
  useAdminHomepageSettings,
  useUploadHomepageMedia,
  useUpdateHomepageSettings,
} from '../hooks/api-hooks';
import { DEFAULT_HOMEPAGE_SETTINGS } from '../lib/homepage-defaults';
import type { HomepageSettings, HomepageSettingsInput } from '../lib/types';

const toInput = (settings: HomepageSettings): HomepageSettingsInput => ({
  heroTitle: settings.heroTitle,
  heroDescription: settings.heroDescription,
  primaryCtaLabel: settings.primaryCtaLabel,
  primaryCtaHref: settings.primaryCtaHref,
  secondaryCtaLabel: settings.secondaryCtaLabel,
  secondaryCtaHref: settings.secondaryCtaHref,
  heroArtLabel: settings.heroArtLabel,
  heroMediaType: settings.heroMediaType,
  heroMediaUrl: settings.heroMediaUrl,
  heroPosterUrl: settings.heroPosterUrl,
  coursesHeading: settings.coursesHeading,
  coursesDescription: settings.coursesDescription,
  skillsHeading: settings.skillsHeading,
  skillsDescription: settings.skillsDescription,
  workshopsHeading: settings.workshopsHeading,
  workshopsDescription: settings.workshopsDescription,
  quoteText: settings.quoteText,
  quoteName: settings.quoteName,
  quoteRole: settings.quoteRole,
  quoteImageUrl: settings.quoteImageUrl,
});

export function AdminHomepageSettingsPage() {
  const settings = useAdminHomepageSettings();
  const save = useUpdateHomepageSettings();
  const heroUpload = useUploadHomepageMedia();
  const portraitUpload = useUploadHomepageMedia();
  const [form, setForm] = useState<HomepageSettingsInput>(() =>
    toInput(DEFAULT_HOMEPAGE_SETTINGS),
  );

  useEffect(() => {
    if (settings.data) setForm(toInput(settings.data));
  }, [settings.data]);

  function setField<K extends keyof HomepageSettingsInput>(
    field: K,
    value: HomepageSettingsInput[K],
  ) {
    setForm((current) => ({ ...current, [field]: value }));
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    save.mutate(form);
  }

  function uploadHero(file?: File) {
    if (!file) return;
    heroUpload.mutate(file, {
      onSuccess: (result) => {
        setField('heroMediaUrl', result.mediaUrl);
        setField('heroMediaType', result.mediaType);
      },
    });
  }

  function uploadPortrait(file?: File) {
    if (!file) return;
    portraitUpload.mutate(file, {
      onSuccess: (result) => setField('quoteImageUrl', result.mediaUrl),
    });
  }

  const preview: HomepageSettings = {
    ...DEFAULT_HOMEPAGE_SETTINGS,
    ...form,
    heroMediaUrl: form.heroMediaUrl || null,
    heroPosterUrl: form.heroPosterUrl || null,
    quoteImageUrl: form.quoteImageUrl || null,
  };

  if (settings.isPending) return <Loading label="Đang tải cài đặt trang chủ…" />;
  if (settings.error) return <ErrorState error={settings.error} />;

  return (
    <section className="admin-settings" aria-labelledby="admin-homepage-title">
      <header className="admin-settings-header">
        <div>
          <h1 id="admin-homepage-title">Cài đặt trang chủ</h1>
          <p className="lead">Chỉnh nội dung và media rồi xem trước trước khi lưu.</p>
        </div>
        <button form="homepage-settings-form" type="submit" disabled={save.isPending}>
          {save.isPending ? 'Đang lưu…' : 'Lưu thay đổi'}
        </button>
      </header>

      <div className="admin-settings-layout">
        <form id="homepage-settings-form" className="admin-settings-form" onSubmit={handleSubmit}>
          <fieldset>
            <legend>Hero</legend>
            <label>
              Tiêu đề
              <textarea
                value={form.heroTitle}
                maxLength={180}
                required
                onChange={(event) => setField('heroTitle', event.target.value)}
              />
            </label>
            <label>
              Mô tả
              <textarea
                value={form.heroDescription}
                maxLength={700}
                required
                onChange={(event) => setField('heroDescription', event.target.value)}
              />
            </label>
            <div className="admin-field-grid">
              <label>
                Nút chính
                <input
                  value={form.primaryCtaLabel}
                  maxLength={80}
                  required
                  onChange={(event) => setField('primaryCtaLabel', event.target.value)}
                />
              </label>
              <label>
                Link nút chính
                <input
                  value={form.primaryCtaHref}
                  required
                  onChange={(event) => setField('primaryCtaHref', event.target.value)}
                />
              </label>
              <label>
                Nút phụ
                <input
                  value={form.secondaryCtaLabel}
                  maxLength={80}
                  required
                  onChange={(event) => setField('secondaryCtaLabel', event.target.value)}
                />
              </label>
              <label>
                Link nút phụ
                <input
                  value={form.secondaryCtaHref}
                  required
                  onChange={(event) => setField('secondaryCtaHref', event.target.value)}
                />
              </label>
            </div>
          </fieldset>

          <fieldset>
            <legend>Ảnh hoặc video nền</legend>
            <label>
              Loại media
              <select
                value={form.heroMediaType}
                onChange={(event) =>
                  setField('heroMediaType', event.target.value as HomepageSettingsInput['heroMediaType'])
                }
              >
                <option value="NONE">Hình trang trí mặc định</option>
                <option value="IMAGE">Ảnh</option>
                <option value="VIDEO">Video nền</option>
              </select>
            </label>
            <label>
              Tải file mới lên R2
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp,image/avif,video/mp4,video/webm"
                disabled={heroUpload.isPending}
                onChange={(event) => uploadHero(event.target.files?.[0])}
              />
            </label>
            {heroUpload.isPending && <p className="muted">Đang tải media lên R2…</p>}
            {heroUpload.error && <ErrorState error={heroUpload.error} />}
            <label>
              URL media công khai
              <input
                type="text"
                inputMode="url"
                placeholder="https://media.huytkdai.com/public/homepage/..."
                value={form.heroMediaUrl ?? ''}
                onChange={(event) => setField('heroMediaUrl', event.target.value)}
              />
            </label>
            <label>
              URL ảnh poster video
              <input
                type="text"
                inputMode="url"
                placeholder="https://.../poster.webp"
                value={form.heroPosterUrl ?? ''}
                onChange={(event) => setField('heroPosterUrl', event.target.value)}
              />
            </label>
            <label>
              Chữ trên media
              <input
                value={form.heroArtLabel}
                maxLength={120}
                onChange={(event) => setField('heroArtLabel', event.target.value)}
              />
            </label>
            <p className="form-help">
              Có thể tải trực tiếp lên R2 hoặc dán URL HTTPS từ Cloudflare Stream. Video tự phát
              ở chế độ tắt tiếng để trình duyệt cho phép chạy nền.
            </p>
          </fieldset>

          <fieldset>
            <legend>Các khu vực nội dung</legend>
            <label>
              Tiêu đề khóa học
              <input
                value={form.coursesHeading}
                required
                onChange={(event) => setField('coursesHeading', event.target.value)}
              />
            </label>
            <label>
              Mô tả khóa học
              <textarea
                value={form.coursesDescription}
                required
                onChange={(event) => setField('coursesDescription', event.target.value)}
              />
            </label>
            <label>
              Tiêu đề skill
              <input
                value={form.skillsHeading}
                required
                onChange={(event) => setField('skillsHeading', event.target.value)}
              />
            </label>
            <label>
              Mô tả skill
              <textarea
                value={form.skillsDescription}
                required
                onChange={(event) => setField('skillsDescription', event.target.value)}
              />
            </label>
            <label>
              Tiêu đề workshop
              <input
                value={form.workshopsHeading}
                required
                onChange={(event) => setField('workshopsHeading', event.target.value)}
              />
            </label>
            <label>
              Mô tả workshop
              <textarea
                value={form.workshopsDescription}
                required
                onChange={(event) => setField('workshopsDescription', event.target.value)}
              />
            </label>
          </fieldset>

          <fieldset>
            <legend>Lời giới thiệu</legend>
            <label>
              Nội dung trích dẫn
              <textarea
                value={form.quoteText}
                required
                onChange={(event) => setField('quoteText', event.target.value)}
              />
            </label>
            <div className="admin-field-grid">
              <label>
                Tên
                <input
                  value={form.quoteName}
                  required
                  onChange={(event) => setField('quoteName', event.target.value)}
                />
              </label>
              <label>
                Vai trò
                <input
                  value={form.quoteRole}
                  required
                  onChange={(event) => setField('quoteRole', event.target.value)}
                />
              </label>
            </div>
            <label>
              Tải ảnh chân dung lên R2
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp,image/avif"
                disabled={portraitUpload.isPending}
                onChange={(event) => uploadPortrait(event.target.files?.[0])}
              />
            </label>
            {portraitUpload.isPending && <p className="muted">Đang tải ảnh lên R2…</p>}
            {portraitUpload.error && <ErrorState error={portraitUpload.error} />}
            <label>
              URL ảnh chân dung
              <input
                type="text"
                inputMode="url"
                placeholder="https://.../portrait.webp"
                value={form.quoteImageUrl ?? ''}
                onChange={(event) => setField('quoteImageUrl', event.target.value)}
              />
            </label>
          </fieldset>

          {save.error && <ErrorState error={save.error} />}
          {save.isSuccess && (
            <p className="success" role="status">
              Đã cập nhật trang chủ.
            </p>
          )}
        </form>

        <aside className="admin-preview" aria-label="Xem trước hero">
          <div className="admin-preview-sticky">
            <span className="preview-label">Xem trước</span>
            <div className="admin-preview-card">
              <div>
                <h2>{preview.heroTitle}</h2>
                <p>{preview.heroDescription}</p>
                <div className="hero-actions">
                  <span className="btn">{preview.primaryCtaLabel}</span>
                  <span className="btn btn-quiet">{preview.secondaryCtaLabel}</span>
                </div>
              </div>
              <HomepageHeroMedia settings={preview} />
            </div>
          </div>
        </aside>
      </div>
    </section>
  );
}
